import React, { useState, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { Form, Modal, Button, Spin, notification } from 'antd';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  editFarmLocation,
  showFarmLocation,
  updateFarmLocation,
  storeFarmLocation,
  activateFarmLocation,
} from '@app/api/farmLocations.api';
import { FarmLocationRequest } from '@app/models/farm_locations/FarmLocationRequest';
import { FarmLocationApiData } from '@app/models/farm_locations/FarmLocationresponse';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface FarmLocationsProps {
  id?: string;
  editable: boolean;
  pageTitle: string;
  type: 'edit' | 'create' | 'view';
  setCurrentBreadcrumName: (name: string) => void;
}

const FarmLocations = React.forwardRef<any, FarmLocationsProps>(
  ({ editable, pageTitle, id, type, setCurrentBreadcrumName }, ref) => {
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [description, setDescription] = useState('');
    const [contactNo, setContactNo] = useState('');
    const [faxNo, setFaxNo] = useState('');
    const [code, setCode] = useState('');
    const [status, setStatus] = useState('');
    const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isMapModalVisible, setIsMapModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const { addHeaderButton, removeHeaderButton } = useHeader();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Map references
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);

    const getUserCustomerId = useCallback((): number => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.customer?.id || 0;
      } catch (error) {
        console.error('Error getting customer ID:', error);
        return 0;
      }
    }, []);

    const fetchFarmLocation = useCallback(async () => {
      if (type === 'create') return;

      setLoading(true);
      try {
        let response: FarmLocationApiData | undefined;

        if (id) {
          response = type === 'edit' ? await editFarmLocation(parseInt(id)) : await showFarmLocation(parseInt(id));
        }

        if (response?.pr_location) {
          const location = response.pr_location;

          setName(location.name || '');
          setAddress(location.address || '');
          setDescription(location.description || '');
          setContactNo(location.contact_no || '');
          setFaxNo(location.fax_no || '');
          setCode(location.code || '');
          setStatus(location.status || '');

          // Parse coordinates
          if (location.latitude && location.longitude) {
            setGpsLocation({
              lat: parseFloat(location.latitude.toString()),
              lng: parseFloat(location.longitude.toString()),
            });
          }

          // Set form values
          form.setFieldsValue({
            name: location.name || '',
            address: location.address || '',
            description: location.description || '',
            contact_no: location.contact_no || '',
            fax_no: location.fax_no || '',
            code: location.code || '',
            status: location.status || '',
          });

          if (location.can_activate) {
            addHeaderButton(
              'activate-button',
              <BaseButton type="primary" onClick={() => console.log('Activate clicked')} size="small">
                {t('Activate')}
              </BaseButton>,
            );
          }

          if (location.can_activate) {
            addHeaderButton(
              'activate-button',
              <BaseButton
                type="primary"
                onClick={async () => {
                  try {
                    setLoading(true);
                    await activateFarmLocation(location.id);
                    notification.success({
                      message: t('Success'),
                      description: t('Farm location activated successfully'),
                    });
                    navigate('/all-farms');
                    //await fetchFarmLocation();
                  } catch (error: any) {
                    notification.error({
                      message: t('Error'),
                      description: error.message || t('Failed to activate farm location'),
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
                size="small"
              >
                {t('Activate')}
              </BaseButton>,
            );
          }
        }
      } catch (error) {
        console.error(`Error fetching farm location for ${type} mode:`, error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch farm location data',
        });
      } finally {
        setLoading(false);
      }
    }, [type, id, form]);

    const handleSubmit = useCallback(async () => {
      if (!name.trim()) {
        notification.error({
          message: 'Validation Error',
          description: 'Farm location name is required',
        });
        return;
      }

      if (!address.trim()) {
        notification.error({
          message: 'Validation Error',
          description: 'Address is required',
        });
        return;
      }

      if (!gpsLocation) {
        notification.error({
          message: 'Validation Error',
          description: 'GPS location is required',
        });
        return;
      }

      const customerId = getUserCustomerId();
      if (!customerId) {
        notification.error({
          message: 'Error',
          description: 'Customer information not found',
        });
        return;
      }

      const formattedPayload: FarmLocationRequest = {
        customer_id: customerId,
        name: name.trim(),
        description: description.trim(),
        address: address.trim(),
        contact_no: contactNo.trim(),
        fax_no: faxNo.trim(),
        coordinates: {
          lat: gpsLocation.lat.toString(),
          lng: gpsLocation.lng.toString(),
        },
      };

      console.log('Submitting payload:', formattedPayload);

      setLoading(true);
      try {
        if (type === 'edit' && id) {
          await updateFarmLocation(parseInt(id), formattedPayload);
          notification.success({
            message: 'Success',
            description: 'Farm location updated successfully',
          });
        } else {
          await storeFarmLocation(formattedPayload);
          notification.success({
            message: 'Success',
            description: 'Farm location created successfully',
          });
        }

        navigate('/all-farms');
      } catch (error: unknown) {
        console.error('Error submitting data:', error);
        notification.error({
          message: 'Error',
          description: error instanceof Error ? error.message : 'Failed to submit farm location data',
        });
      } finally {
        setLoading(false);
      }
    }, [name, address, gpsLocation, contactNo, faxNo, type, id, getUserCustomerId, description, navigate]);

    // Map initialization and management
    useEffect(() => {
      if (!isMapModalVisible || !mapRef.current) return;

      // Clean up existing map instance
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerRef.current = null;
      }

      // Create new map instance
      mapInstance.current = L.map(mapRef.current).setView([6.9271, 79.8612], 13);

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {}).addTo(mapInstance.current);

      // Add existing marker if GPS location exists
      if (gpsLocation) {
        markerRef.current = L.marker([gpsLocation.lat, gpsLocation.lng]).addTo(mapInstance.current);
        mapInstance.current.setView([gpsLocation.lat, gpsLocation.lng], 13);
      }

      // Handle map clicks
      mapInstance.current.on('click', (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        const tempLocation = { lat, lng };

        // Update or create marker
        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          if (mapInstance.current) {
            markerRef.current = L.marker([lat, lng]).addTo(mapInstance.current);
          }
        }

        setGpsLocation(tempLocation);
      });

      // Fix map size issues
      setTimeout(() => {
        if (mapInstance.current) {
          mapInstance.current.invalidateSize();
        }
      }, 300);

      return () => {
        // Cleanup function
      };
    }, [isMapModalVisible, gpsLocation]);

    // Fetch data on component mount
    useEffect(() => {
      fetchFarmLocation();
    }, [fetchFarmLocation]);

    useEffect(() => {
      if (editable) {
        addHeaderButton(
          'save-button',
          <BaseButton type="primary" htmlType="submit" onClick={handleSubmit} loading={loading} size="small">
            {t('Save')}
          </BaseButton>,
        );

        addHeaderButton(
          'cancel-button',
          <BaseButton onClick={() => navigate('/all-farms')} style={{ marginRight: '8px' }} size="small">
            {t('Cancel')}
          </BaseButton>,
        );
      }

      if (!editable && id) {
        addHeaderButton(
          'edit-button',
          <BaseButton type="primary" onClick={() => navigate(`/edit-farm`, { state: { id } })} size="small">
            {t('Edit')}
          </BaseButton>,
        );
      }

      return () => {
        removeHeaderButton('save-button');
        removeHeaderButton('cancel-button');
        removeHeaderButton('edit-button');
      };
    }, [form, loading, editable, id, addHeaderButton, t, navigate, removeHeaderButton, handleSubmit]);

    // Map modal handlers
    const showMapModal = () => {
      setIsMapModalVisible(true);
    };

    const handleMapModalOk = () => {
      setIsMapModalVisible(false);
    };

    const handleMapModalCancel = () => {
      setIsMapModalVisible(false);
    };

    if (loading && type !== 'create') {
      return (
        <BaseCard title={pageTitle}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </BaseCard>
      );
    }

    return (
      <>
        <BaseCard title={pageTitle}>
          <Form
            form={form}
            labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
            labelAlign="left"
            labelWrap
            wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
            size="small"
          >
            <BaseRow gutter={[16, 16]}>
              {!editable && (
                <BaseCol xs={24} md={12}>
                  <Form.Item label="Farm Code" name="code">
                    <BaseInput readOnly value={code} />
                  </Form.Item>
                </BaseCol>
              )}

              <BaseCol xs={24} md={12}>
                <Form.Item name="name" label="Farm Location Name" required>
                  {editable ? (
                    <BaseInput
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter farm location name"
                    />
                  ) : (
                    <span style={{ fontWeight: 'normal' }}>{name || '-'}</span>
                  )}
                </Form.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <Form.Item label="Description" name="description">
                  {editable ? (
                    <BaseInput.TextArea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter description"
                      rows={3}
                    />
                  ) : (
                    <span style={{ fontWeight: 'normal' }}>{description || '-'}</span>
                  )}
                </Form.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <Form.Item label="Address" name="address" required>
                  {editable ? (
                    <BaseInput.TextArea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter detailed address"
                      rows={3}
                    />
                  ) : (
                    <span style={{ fontWeight: 'normal' }}>{address || '-'}</span>
                  )}
                </Form.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <Form.Item label="Contact No" name="contact_no">
                  <BaseInput
                    value={contactNo}
                    onChange={(e) => setContactNo(e.target.value)}
                    readOnly={!editable}
                    placeholder="Enter contact number"
                  />
                </Form.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <Form.Item label="Fax" name="fax_no">
                  <BaseInput
                    value={faxNo}
                    readOnly={!editable}
                    onChange={(e) => setFaxNo(e.target.value)}
                    placeholder="Enter fax number"
                  />
                </Form.Item>
              </BaseCol>

              <BaseCol xs={24} md={12}>
                <Form.Item label="GPS Location" name="gpsLocation" required>
                  <BaseInput
                    value={gpsLocation ? `${gpsLocation.lat.toFixed(6)}, ${gpsLocation.lng.toFixed(6)}` : ''}
                    readOnly
                    placeholder="No location selected"
                    style={{ marginBottom: '8px' }}
                  />
                  {editable && (
                    <Button type="primary" onClick={showMapModal} disabled={!editable}>
                      Select Location
                    </Button>
                  )}
                </Form.Item>
              </BaseCol>

              {!editable && (
                <BaseCol xs={24} md={12}>
                  <Form.Item label="Status" name="status">
                    <BaseInput readOnly value={status} />
                  </Form.Item>
                </BaseCol>
              )}
            </BaseRow>
          </Form>
        </BaseCard>

        {/* Map Selection Modal */}
        <Modal
          title="Select GPS Location"
          open={isMapModalVisible}
          onOk={handleMapModalOk}
          onCancel={handleMapModalCancel}
          width={800}
          bodyStyle={{ padding: '12px' }}
          footer={[
            <Button key="cancel" onClick={handleMapModalCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={handleMapModalOk}>
              Confirm Location
            </Button>,
          ]}
        >
          <div style={{ marginBottom: '10px' }}>
            Click on the map to select a location. The coordinates will be shown here:
            {gpsLocation && (
              <div style={{ fontWeight: 'bold', margin: '5px 0' }}>
                Latitude: {gpsLocation.lat.toFixed(6)}, Longitude: {gpsLocation.lng.toFixed(6)}
              </div>
            )}
          </div>
          <div
            ref={mapRef}
            style={{
              height: '500px',
              width: '100%',
              border: '1px solid #ccc',
            }}
          />
        </Modal>
      </>
    );
  },
);

FarmLocations.displayName = 'FarmLocations';

export default FarmLocations;
